/**
 * Search Store
 *
 * Manages search state including:
 * - Current search filters
 * - Search history
 * - Recent searches per country
 *
 * @module @schoolidb/store/searchStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CountryCode, SchoolType, BoardType } from '@schoolidb/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Search filters
 */
export interface SearchFilters {
  /** Search query */
  query: string;
  /** School types */
  schoolTypes: SchoolType[];
  /** Board types */
  boards: BoardType[];
  /** Location (city/area) */
  location: string | null;
  /** Minimum rating */
  minRating: number | null;
  /** Fee range */
  feeRange: {
    min: number | null;
    max: number | null;
  };
  /** Sort by */
  sortBy: 'relevance' | 'rating' | 'distance' | 'fees_low' | 'fees_high';
}

/**
 * Recent search entry
 */
export interface RecentSearch {
  /** Search query */
  query: string;
  /** Timestamp */
  timestamp: number;
  /** Country code */
  countryCode: CountryCode;
  /** Filters applied */
  filters?: Partial<SearchFilters>;
}

/**
 * Search state
 */
export interface SearchState {
  /** Current filters */
  filters: SearchFilters;
  /** Recent searches by country */
  recentSearches: Record<CountryCode, RecentSearch[]>;
  /** Maximum recent searches to keep */
  maxRecentSearches: number;
}

/**
 * Search actions
 */
export interface SearchActions {
  /** Set search query */
  setQuery: (query: string) => void;
  /** Set filters */
  setFilters: (filters: Partial<SearchFilters>) => void;
  /** Reset filters */
  resetFilters: () => void;
  /** Add to recent searches */
  addRecentSearch: (search: Omit<RecentSearch, 'timestamp'>) => void;
  /** Clear recent searches for a country */
  clearRecentSearches: (countryCode: CountryCode) => void;
  /** Get recent searches for a country */
  getRecentSearches: (countryCode: CountryCode) => RecentSearch[];
}

export type SearchStore = SearchState & SearchActions;

// ============================================================================
// Initial State
// ============================================================================

const defaultFilters: SearchFilters = {
  query: '',
  schoolTypes: [],
  boards: [],
  location: null,
  minRating: null,
  feeRange: { min: null, max: null },
  sortBy: 'relevance',
};

const initialState: SearchState = {
  filters: defaultFilters,
  recentSearches: {} as Record<CountryCode, RecentSearch[]>,
  maxRecentSearches: 10,
};

// ============================================================================
// Store
// ============================================================================

/**
 * Search store with persistence
 */
export const useSearchStore = create<SearchStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setQuery: (query) => {
        set((state) => ({
          filters: { ...state.filters, query },
        }));
      },

      setFilters: (filters) => {
        set((state) => ({
          filters: { ...state.filters, ...filters },
        }));
      },

      resetFilters: () => {
        set({ filters: defaultFilters });
      },

      addRecentSearch: (search) => {
        set((state) => {
          const countrySearches = state.recentSearches[search.countryCode] || [];
          
          // Remove duplicate if exists
          const filtered = countrySearches.filter(
            (s) => s.query.toLowerCase() !== search.query.toLowerCase()
          );
          
          // Add new search at the beginning
          const newSearch: RecentSearch = {
            ...search,
            timestamp: Date.now(),
          };
          
          const updated = [newSearch, ...filtered].slice(0, state.maxRecentSearches);
          
          return {
            recentSearches: {
              ...state.recentSearches,
              [search.countryCode]: updated,
            },
          };
        });
      },

      clearRecentSearches: (countryCode) => {
        set((state) => ({
          recentSearches: {
            ...state.recentSearches,
            [countryCode]: [],
          },
        }));
      },

      getRecentSearches: (countryCode) => {
        return get().recentSearches[countryCode] || [];
      },
    }),
    {
      name: 'schoolidb-search',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        recentSearches: state.recentSearches,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectFilters = (state: SearchStore) => state.filters;
export const selectQuery = (state: SearchStore) => state.filters.query;
export const selectRecentSearches = (countryCode: CountryCode) => (state: SearchStore) =>
  state.recentSearches[countryCode] || [];
